import { Component } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Input, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';

import { languageService, languageSupport } from '@unpispas/upp-base';
import { barcodeService } from '@unpispas/upp-base';

@Component({
    selector: 'upp-barcode-scanning',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-scanning.html',
    styleUrls: [
        './upp-scanning.scss'
    ],
})
export class UppBarcodeScanningComponent extends languageSupport implements OnChanges, OnDestroy {
    private static readonly _BarcodeDigitLengths = [8, 12, 13];
    private static readonly _BarcodeMaxLength = 128;

    /** When true, shows the full-screen waiting overlay. */
    @Input() active = false;

    @Output() OnCode = new EventEmitter<string>();
    @Output() OnCancel = new EventEmitter<void>();

    private _manualDraft = '';

    get ManualDraft(): string {
        return this._manualDraft;
    }

    get ManualErrorKey(): string | null {
        const _error = UppBarcodeScanningComponent._ManualError(this._manualDraft);
        if (_error === 'characters'){
            return '@place_barcode_invalid_characters';
        }

        if (_error === 'length'){
            return '@place_barcode_invalid_length';
        }

        return null;
    }

    get ManualAcceptable(): boolean {
        const _value = this._manualDraft.trim();
        return !!_value && UppBarcodeScanningComponent._ManualError(_value) === null;
    }

    constructor(lang: languageService, change: ChangeDetectorRef, private barcode: barcodeService,) {
        super(lang, change);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!('active' in changes)){
            return;
        }

        if (this.active){
            this._ResetManual();
            this.barcode.enable((code) => this._OnCaptured(code), { mode: 'capture' });
            return;
        }

        this.barcode.stopCapture();
    }

    ngOnDestroy(): void {
        this.barcode.stopCapture();
        this.OnDestroy();
    }

    OnFrameClick(): void {
        this._EmitCancel();
    }

    OnFrameKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape'){
            event.preventDefault();
            this.OnFrameClick();
        }
    }

    OnMessageClick(event: Event): void {
        event.stopPropagation();
    }

    OnManualInput(value: string): void {
        this._manualDraft = value;
        this._chng?.markForCheck();
    }

    OnManualAcceptClick(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        if (!this.ManualAcceptable){
            return;
        }

        const _value = this._manualDraft.trim();
        this._ResetManual();
        this.barcode.stopCapture();
        this.OnCode.emit(_value);
    }

    private static _ManualError(value: string): 'characters' | 'length' | null {
        const _value = value.trim();
        if (!_value){
            return null;
        }

        if (!/^[A-Za-z0-9]+$/.test(_value)){
            return 'characters';
        }

        if (/^\d+$/.test(_value)){
            if (!UppBarcodeScanningComponent._BarcodeDigitLengths.includes(_value.length)){
                return 'length';
            }

            return null;
        }

        if (_value.length > UppBarcodeScanningComponent._BarcodeMaxLength){
            return 'length';
        }

        return null;
    }

    private _OnCaptured(code: string): void {
        const _value = String(code ?? '').trim();
        if (!_value){
            return;
        }

        this._ResetManual();
        this.OnCode.emit(_value);
    }

    private _EmitCancel(): void {
        this.barcode.stopCapture();
        this._ResetManual();
        this.OnCancel.emit();
    }

    private _ResetManual(): void {
        this._manualDraft = '';
        this._chng?.markForCheck();
    }
}
